import { AfterContentInit, Component, ContentChildren, ElementRef, Input, QueryList } from '@angular/core';

import * as Flickity from 'flickity';
import { Observable, Subject } from 'rxjs';

import { BaseComponent } from '../base/base.component';
import { SliderElementComponent } from './slider-element/slider-element.component';
import { SliderOptions } from './slider.interface';
import { SliderService } from './slider.service';

@Component({
    selector: 'trg-slider',
    templateUrl: './slider.component.html',
    styleUrls: ['./slider.component.scss'],
})
export class SliderComponent extends BaseComponent implements AfterContentInit {
    private slider: Flickity = null;

    @Input() sliderClasses: string = '';
    @Input() sliderOptions: SliderOptions = {
        initialIndex: 0,
        draggable: true,
        dragThreshold: 10,
        prevNextButtons: true,
        groupCells: false,
        cellAlign: 'center',
        contain: false,
        percentPosition: false,
        cellSelector: '.slider-cell',
        pageDots: false,
        watchCSS: false,
        wrapAround: true,
        roundedButton: false,
        lazyLoad: 1,
        whiteTheme: false,
        compactMode: false,
    };
    loadingSlider: boolean = false;
    minHeight: string;

    public UID: string = String(Math.round(Math.random() * 1000000)); // NOSONAR

    @Input('options') set setOptions(options: SliderOptions) {
        this.sliderOptions = Object.assign(this.sliderOptions, options);
    }

    @Input('tabletOptions') sliderTabletOptions: SliderOptions = null;

    @Input('desktopOptions') sliderDesktopOptions: SliderOptions = null;

    @Input('initialIndex') sliderInitialIndex: number = 0;

    @Input('instanceName') sliderInstanceName: string = `slider-${this.UID}`;

    constructor(protected elementRef: ElementRef, private sliderService: SliderService) {
        super(elementRef);
    }

    @ContentChildren(SliderElementComponent) sliderElements: QueryList<SliderElementComponent>;
    public slidesList: SliderElementComponent[];

    public ngAfterContentInit(): void {
        const options = this.sliderOptions,
            slidesCount = this.sliderElements.toArray().length;

        if (this.sliderInitialIndex && this.sliderInitialIndex > 0) {
            options.initialIndex = this.sliderInitialIndex - 1;
            options.initialIndex = options.initialIndex > slidesCount ? slidesCount : options.initialIndex;
        }

        // Tablet config
        if (this.sliderTabletOptions && matchMedia('screen and (min-width: 769px)').matches) {
            Object.assign(options, this.sliderTabletOptions);
        }

        // Desktop config
        if (this.sliderDesktopOptions && matchMedia('screen and (min-width: 1088px)').matches) {
            Object.assign(options, this.sliderDesktopOptions);
        }

        this.updateSliderItems();

        this.sliderElements.changes.subscribe(() => {
            this.updateSliderItems();
        });
    }

    private createSlider(): void {
        const mainClass = `.slider-${this.UID}`;
        const options = this.sliderOptions;

        this.slidesList = this.sliderElements.toArray();

        this.checkContentListLength().subscribe(() => {
            this.slider = new Flickity(mainClass, options);
            this.sliderService.setInstance(this.sliderInstanceName, this.slider);
            this.checkCarouselLength();
        });
    }

    private updateSliderItems(): void {
        this.loadingSlider = true;

        if (this.slider && Object.keys(this.slider).length) {
            this.slidesList = [];
            this.slider.destroy();
            this.checkContentListLength(true).subscribe(() => {
                this.createSlider();
            });
        } else {
            this.createSlider();
        }
    }

    private checkContentListLength(empty?: boolean): Observable<boolean> {
        const varToCheck: number = empty ? 0 : this.sliderElements.toArray().length;
        const contentListContent: number = this.slidesList.length;
        const isConditionFilled: Subject<any> = new Subject<any>();

        const interval = setInterval(() => {
            if (contentListContent === varToCheck) {
                clearInterval(interval);
                isConditionFilled.next(true);
            }
        }, 50); // 50 = perfect value to observe changes and apply them quickly

        return isConditionFilled.asObservable();
    }

    private checkCarouselLength(): void {
        this.slider.on('lazyLoad', (event, cellElement) => {
            if (this.slider) {
                const cellsHeight = this.slider.maxCellHeight || 0;
                const dotsHeight = !!this.slider.pageDots ? this.slider.pageDots.holder.clientHeight : 0;
                this.minHeight = `${cellsHeight + dotsHeight}px`; // @TODO on mur-sensation
            }
        });
        const interval = setInterval(() => {
            if (this.slider && Object.keys(this.slider).length) {
                this.loadingSlider = false;

                if (this.sliderOptions.roundedButton && this.slider.prevButton && this.slider.nextButton) {
                    const buttons = [this.slider.prevButton.element, this.slider.nextButton.element];
                    this.setClasses(buttons, ['rounded']);
                }

                if (!!this.slider.pageDots) {
                    const dots = [this.slider.pageDots.holder];
                    const dotsClasses = [];
                    if (this.sliderOptions.whiteTheme) {
                        dotsClasses.push('is-white');
                    }
                    if (this.sliderOptions.compactMode) {
                        dotsClasses.push('close-dots');
                    }
                    if (dotsClasses) {
                        this.setClasses(dots, dotsClasses);
                    }
                }

                this.slider.resize(); // Necessary for setting position

                const groupCells = this.slider.options ? this.slider.options.groupCells : null;
                const cellsDisplayed = this.sliderOptions.cellsDisplayed;
                const nextButton = this.slider.nextButton || {};
                const prevButton = this.slider.prevButton || {};

                //We need these params to prevent the carousel of sliding to much using the event below
                if (typeof groupCells === 'number' && cellsDisplayed && nextButton.element && prevButton.element) {
                    //check if buttons are needed at init
                    this.updateButtons(
                        [nextButton.element, prevButton.element],
                        cellsDisplayed,
                        this.slider.cells.length,
                    );
                    this.slider.on('select', (index) => {
                        if (
                            this.updateButtons(
                                [nextButton.element, prevButton.element],
                                cellsDisplayed,
                                this.slider.cells.length,
                            )
                        ) {
                            return;
                        }
                        if (groupCells >= 1) {
                            const cellIndex = index * groupCells;
                            if (cellIndex >= this.slider.cells.length - cellsDisplayed) {
                                nextButton.element.setAttribute('disabled', '');
                                this.slider.selectedIndex = this.slider.cells.length - cellsDisplayed;
                            } else {
                                nextButton.element.removeAttribute('disabled');
                            }
                        }
                    });
                }
                clearInterval(interval);
            }
        }, 50);
    }

    private updateButtons(buttons: HTMLElement[], cellsDisplayed: number, slidesLength: number): boolean {
        let buttonsDisabled = false;
        if (cellsDisplayed === slidesLength) {
            //check if buttons are needed
            buttons[0].setAttribute('disabled', '');
            buttons[1].setAttribute('disabled', '');
            buttonsDisabled = true;
        }
        return buttonsDisabled;
    }

    private setClasses(elements: HTMLElement[], classes: string[]): void {
        for (let i in elements) {
            if (elements[i].classList) {
                for (let j in classes) {
                    elements[i].classList.add(classes[j]);
                }
            }
        }
    }
}
